import { Controller } from 'react-hook-form';
import { ageRangeOptions } from '../../schemas/employees-report-schemas';
import SearchTextField from 'Components/SeachTextField/SearchTextField';
import CustomDatePicker from 'Components/CustomDatePicker/CustomDatePicker';
import CustomSelect from 'Components/CustomSelect/CustomSelect';
import CustomMultipleSelect from 'Components/CustomMultipleSelect/CustomMultipleSelect';
import type { EmployeeReportFilterI } from 'types';
import { Search } from '@mui/icons-material';
import useEmployeesReportFilters from '../../../../../../Hooks/useEmployeesReportFilters';
import { ReportsLayoutFiltersContainer, ResetButton, SearchButton } from './EmployeesReportFilters.styled';

interface Props {
  onSearch: (filters: EmployeeReportFilterI) => void;
  onFiltersChange?: (filters: EmployeeReportFilterI) => void;
}

const EmployeesReportFilters = ({ onSearch }: Props) => {
  const {
    control,
    isDirty,
    handleReset,
    setValue,
    getValues,
    terminalsData,
    departmentsData,
    jobTitlesData,
    jobTitlesTypesData,
    t2,
  } = useEmployeesReportFilters({ onSearch });

  return (
    <ReportsLayoutFiltersContainer>
      <Controller
        name="fullName"
        control={control}
        render={({ field }) => <SearchTextField placeholder={t2('name_or_payroll')} {...field} />}
      />

      <Controller
        name="entryDate"
        control={control}
        render={({ field }) => <CustomDatePicker label={t2('entry_date')} placeholder="dd/mm/yyyy" {...field} />}
      />

      <Controller
        name="terminalId"
        control={control}
        render={({ field }) => (
          <CustomMultipleSelect
            placeholder={t2('terminal')}
            options={terminalsData?.map(terminal => ({
              value: terminal.id,
              label: terminal.name,
            }))}
            {...field}
          />
        )}
      />

      <Controller
        name="departmentId"
        control={control}
        render={({ field }) => (
          <CustomSelect
            placeholder={t2('department')}
            options={departmentsData?.map(department => ({
              value: department.id,
              label: department.name,
            }))}
            {...field}
          />
        )}
      />

      <Controller
        name="jobTitleId"
        control={control}
        render={({ field }) => (
          <CustomSelect
            placeholder={t2('job_title')}
            options={jobTitlesData?.map(jobTitle => ({
              value: jobTitle.id,
              label: jobTitle.name,
            }))}
            {...field}
          />
        )}
      />

      <Controller
        name="jobTitleTypeId"
        control={control}
        render={({ field }) => (
          <CustomSelect
            placeholder={t2('job_title_type')}
            options={jobTitlesTypesData?.map(jobTitleType => ({
              value: jobTitleType.id,
              label: jobTitleType.name,
            }))}
            {...field}
          />
        )}
      />

      <Controller
        name="gender"
        control={control}
        render={({ field }) => (
          <CustomSelect
            placeholder={t2('gender_placeholder')}
            options={[
              { value: 0, label: t2('male') },
              { value: 1, label: t2('female') },
            ]}
            {...field}
          />
        )}
      />

      <Controller
        name="ageRange"
        control={control}
        render={({ field }) => (
          <CustomSelect
            placeholder={t2('age_range')}
            options={[
              { value: 0, label: t2('young') },
              { value: 1, label: t2('adult') },
              { value: 2, label: t2('elder') },
            ]}
            {...field}
            onChange={event => {
              field.onChange(event);
              const value = event?.target?.value;

              if (value === null) {
                setValue('minAge', null);
                setValue('maxAge', null);
                return;
              }

              const [min, max] = ageRangeOptions[value];
              setValue('minAge', min);
              setValue('maxAge', max);
            }}
          />
        )}
      />

      <SearchButton
        variant="outlined"
        color="success"
        hidden={!isDirty}
        startIcon={<Search />}
        onClick={() => {
          const values = getValues();
          onSearch(values);
        }}
      >
        {t2('search')}
      </SearchButton>

      {isDirty && (
        <ResetButton onClick={handleReset} variant="outlined">
          {t2('clear_filters')}
        </ResetButton>
      )}
    </ReportsLayoutFiltersContainer>
  );
};

export default EmployeesReportFilters;
